import React from 'react';
import { Badge, Box, Button, Divider, Fade, ListItem, styled, Typography } from '@mui/material';
import clsx from 'clsx';
import { Link } from './Link';

// TODO: tmp solution, material-ui haven't proper types at the moment
const MuiButton = (props: any) => <Button {...props} />;

const PREFIX = 'AppDrawerNavItem';

const classes = {
    label: `${PREFIX}-label`,
    item: `${PREFIX}-item`,
    itemLeaf: `${PREFIX}-itemLeaf`,
    typography: `${PREFIX}-typography`,
    typographyRoot: `${PREFIX}-typographyRoot`,
    button: `${PREFIX}-button`,
    buttonLeaf: `${PREFIX}-buttonLeaf`,
    depthItemLeaf: `${PREFIX}-depthItemLeaf`,
    activeButtonLeaf: `${PREFIX}-activeButtonLeaf`,
    buttonLeafIcon: `${PREFIX}-buttonLeafIcon`,
    active: `${PREFIX}-active`
};

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-colorSecondary': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.text.primary
    }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    [`& .${classes.label}`]: {
        width: '100%'
    },
    [`&.${classes.item}`]: {
        display: 'block',
        width: '100%',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    [`&.${classes.itemLeaf}`]: {
        width: '100%',
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0
    },
    [`&.${classes.depthItemLeaf}`]: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    [`& .AppDrawerNavItem-active > .${classes.typography}`]: {
        color: theme.palette.primary.main
    },
    [`& .${classes.typography}`]: {
        marginLeft: theme.spacing(1.5),
        color: theme.palette.primary.contrastText
    },
    [`& .${classes.typographyRoot}`]: {
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(1, 2)
    },
    [`& .${classes.button}`]: {
        letterSpacing: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        width: '100%'
    },
    [`& .${classes.buttonLeaf}`]: {
        letterSpacing: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        width: '100%',
        padding: '0',
        height: theme.spacing(5),
        borderRadius: '0',
        backgroundColor: 'inherit',
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark
        },
        [`&:hover > .${classes.typography}`]: {
            color: theme.palette.text.primary
        },
        [`&:hover > .${classes.buttonLeafIcon}`]: {
            color: theme.palette.text.primary
        }
    },
    [`& .${classes.activeButtonLeaf}`]: {
        backgroundColor: '#d99889',
        color: theme.palette.primary.main
    },
    [`& .AppDrawerNavItem-active > .${classes.buttonLeafIcon}`]: {
        color: theme.palette.primary.main
    },
    [`& .${classes.buttonLeafIcon}`]: {
        color: theme.palette.primary.contrastText,
        fontSize: '20px'
    },
    [`& .${classes.active}`]: {
        color: theme.palette.primary.main,
        fontWeight: 500
    }
}));

interface AppDrawerNavItemProps {
    depth?: number;
    href?: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    title?: string;
    Icon?: React.ComponentClass<{ className: string }>;
    children?: React.ReactElement | null;
    key: any;
    isActive: boolean;
    open?: boolean;
    isNew?: boolean;
}

export function AppDrawerNavItem(props: AppDrawerNavItemProps) {
    const { children, depth, href, Icon, open, onClick, title, isActive, isNew } = props;

    const style: React.CSSProperties = {
        paddingLeft: 16
    };

    if (href) {
        return (
            <React.Fragment>
                <StyledListItem
                    className={clsx(classes.itemLeaf, {
                        [classes.depthItemLeaf]: href && depth === 0
                    })}
                    disableGutters
                >
                    <MuiButton
                        component={Link}
                        href={href}
                        color={isActive ? 'primary' : 'secondary'}
                        activeClassName={classes.active}
                        className={clsx(classes.buttonLeaf, `depth-${depth}`, {
                            [classes.activeButtonLeaf]: isActive
                        })}
                        classes={{ label: classes.label }}
                        fullWidth
                        onClick={onClick}
                        style={style}
                    >
                        <Icon className={classes.buttonLeafIcon} />
                        <Typography variant="body2" className={classes.typography}>
                            {title}
                        </Typography>
                        {isNew && (
                            <StyledBadge badgeContent="new!" color={isActive ? 'primary' : 'secondary'}>
                                <Box width="1.8em" />
                            </StyledBadge>
                        )}
                    </MuiButton>
                </StyledListItem>
                {depth === 0 && <Divider />}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <StyledListItem className={classes.item} disableGutters>
                {title && open && (
                    <Fade in={open}>
                        <Typography variant="body2" className={classes.typographyRoot}>
                            {title}
                        </Typography>
                    </Fade>
                )}
                {children}
            </StyledListItem>
            <Divider sx={{ backgroundColor: 'primary.contrastText' }} />
        </React.Fragment>
    );
}
